import React, { FC, useContext, useEffect, useState } from 'react';

import { getUrl, IDENTIFIERS } from '@helpers/urlsHelper';
import Buttons from '@components/Buttons';
import Signature from '@components/Icons/Signature';
import CheckCircle from '@components/Icons/CheckCircle';
import CaretRight from '@components/Icons/CaretRight';
import { useRouter } from 'next/router';
import { CustomTerms, CustomTermsTemplate, MESSAGE_ATTACHMENT_REFERENCE_TYPES } from '@typings/api-models';
import skipCustomTerms from '@tsClient/customTerms/skipCustomTerms';
import { useOpenCustomTermsPane, useOpenPreviewDocumentPane } from '@panes/hooks';
import SkipTaskConfirmationModal from '@modals/SkipTaskConfirmationModal';
import UserProfileContext from '@layouts/UserProfileLayout/UserProfileContext';
import * as format from '@format';
import useNoodleApi from '@/hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import s from './CustomTermsCard.module.scss';
import CircularProgress from '../CircularProgress';

type Props = {
  customTerms: Pick<CustomTerms,
    | 'id'
    | 'termsText'
    | 'agreedAt'
    | 'skippedAt'
    | 'documentId'
    | 'isGenerating'
  >;
  template: Pick<CustomTermsTemplate, 'id' | 'title'> & {
    creator: { id: string; slug: string; name?: string };
  };
  setIsFulfilled?: (f: boolean) => void;
  setIsSkipped?: (f: boolean) => void;
  onChange?: () => void;
  canClick?: boolean;
  canSkip: boolean;
  isInDashboard?: boolean;
};

const CustomTermsCard: FC<Props> = ({ setIsFulfilled, setIsSkipped, onChange, customTerms, template, canClick, canSkip, isInDashboard }) => {
  const router = useRouter();
  const openCustomTermsPane = useOpenCustomTermsPane();
  const openDocumentPane = useOpenPreviewDocumentPane();
  const [skipTaskModalOpen, setSkipTaskModalOpen] = useState(false);
  const { isInUserProfile } = useContext(UserProfileContext);
  const {
    data: customTermsVariablesData,
    getData: getCustomTermsVariables,
    fetchingState: { isFetching },
  } = useNoodleApi(tsClient.customTerms.getCustomTermsVariables);

  const hasAgreed = Boolean(customTerms.agreedAt);
  const isSkipped = Boolean(customTerms.skippedAt);
  const { isGenerating } = customTerms;

  useEffect(() => {
    if (customTerms.id) {
      getCustomTermsVariables({ customTermsId: customTerms.id, page: 1, perPage: 1_000 });
    }
  }, [customTerms.id, getCustomTermsVariables]);

  useEffect(() => {
    setIsFulfilled?.(hasAgreed);
  }, [hasAgreed, setIsFulfilled]);

  useEffect(() => {
    setIsSkipped?.(isSkipped);
  }, [isSkipped, setIsSkipped]);

  const handleSkipTask = async (): Promise<void> => {
    await skipCustomTerms({ id: customTerms.id });
    setSkipTaskModalOpen(false);
    if (onChange) onChange();
  };

  const handleClick = (): void => {
    if(isInDashboard || isInUserProfile) {
      if (customTerms.documentId) {
        openDocumentPane({ previewDocumentId: customTerms.documentId });
      } else {
        openCustomTermsPane({ customTermsId: customTerms.id });
      }
    } else{
      router.push(
        getUrl(IDENTIFIERS.CUSTOM_TERMS_DETAILS, {
          creatorSlug: template.creator.slug,
          customTermsId: customTerms.id,
        }),
      );
    }
  };

  const getCustomerTitle = (): string => {
    if (template.creator.name) {
      if (isGenerating) {
        return `${template.creator.name} is generating your agreement.`;
      }
      return `${template.creator.name} ${hasAgreed ? 'has requested' : 'is requesting'} you to sign the agreement`;
    }
    return 'Agreement to sign';
  };

  const getCustomerDescription = (): string => {
    if (isGenerating) {
      return template.title || 'Custom terms';
    }
    return hasAgreed ? `${template.title} is signed` : `Please sign ${template.title}`;
  };

  const variablesArray = (customTermsVariablesData?.items || []) as {
    id: string;
    variableName: string;
    value: string | null;
    variableType: string | null;
    filledAt: Date | null;
    filledBy: string | null;
    signatoryName: string | null;
    assignedToPerson: string | null;
  }[];
  const filteredVariables = variablesArray.filter(
    (v) => v.variableName !== 'signatorySignature' && v.variableType === 'signature',
  );
  const total = filteredVariables.length;
  const filled = filteredVariables.filter((v) => v.value !== null && v.value !== '').length;
  const progress = Math.round((filled / total) * 100);

  return (
    <>
      {skipTaskModalOpen && (
        <SkipTaskConfirmationModal
          onClose={() => setSkipTaskModalOpen(false)}
          onConfirm={handleSkipTask}
          taskType={MESSAGE_ATTACHMENT_REFERENCE_TYPES.CUSTOM_TERMS}
        />
      )}
      <Buttons
        disabled={!canClick || (isSkipped && !canSkip)}
        className={s.container}
        isFullWidth
        isWrapper
        onClick={handleClick}
        style={isSkipped ? { backgroundColor: 'var(--color-gray-25)', borderRadius: 8 } : {}}
      >
        {hasAgreed
          ? <CheckCircle size={16} weight="fill" color="var(--color-success)" />
          : <figure>
            <Signature weight="fill" color='var(--color-primary)' size={20} />
          </figure>}
        <div className={s.paymentText}>
          {isInDashboard
            ? (
              <div className={isGenerating ? s.isGenerating : undefined}>
                <p
                  className={hasAgreed ? "caption-bold" : "body-sm-bold"}
                  style={hasAgreed ? { color: 'var(--color-gray-75)' } : { lineHeight: 1.4 }}
                >{template.title}</p>
                {isGenerating && <p className='caption'>Click here to generate</p>}
                {customTerms.agreedAt && <p className="caption">Completed on {format.datetimeLocalTZ.withoutTimezone({ datetime: customTerms.agreedAt })}</p>}
              </div>
            )
            : (
              <>
                <p className="caption">
                  {getCustomerTitle()}
                </p>
                <p className="body-sm-bold">
                  {getCustomerDescription()}
                </p>
              </>
            )}
          {isSkipped && <p className='caption'>This task is no longer required.</p>}
        </div>
        {canClick && (!isSkipped || canSkip) && (
          <>
            {hasAgreed
              ? <CaretRight size={12} color="var(--color-gray-100)" />
              : <div
                className={s.open}
                {...!hasAgreed && !isInDashboard && {
                  style: {
                    backgroundColor: 'var(--color-primary)',
                    border: '1px solid var(--color-primary)',
                    color: 'var(--color-gray-0)',
                  },
                }}
              >
                View
              </div>}
          </>
        )}
        {total === 2 && !isFetching && (
          <div className={s.progressContainer}>
            <CircularProgress progress={progress} size={20} />
            <span className={s.progressText}>
              {filled}/2 has signed.
              {filled === 1 && (
                <>
                  {' '}
                  <b>
                    {filteredVariables
                      .filter(v => !v.value)
                      .map(v => v.assignedToPerson)
                      .filter(Boolean)
                      .join(', ')}
                  </b>{' '}
                remaining
                </>
              )}
            </span>
          </div>
        )}
      </Buttons>
      {canSkip && !isSkipped && (
        <Buttons
          onClick={(event) => {
            event.stopPropagation();
            setSkipTaskModalOpen(true);
          }}
          className={s.skip}
        >
          Skip
        </Buttons>
      )}
    </>
  );
};

export default CustomTermsCard;
